import type { PublishedTermsDescriptor } from "@shared/types/yaas";
import { Button, Checkbox, Label } from "@solomon-labs/design/components/ui";
import React, { useEffect, useId, useRef, useState } from "react";

import { TermsOfServiceModal } from "@/components/TermsOfServiceModal";
import type { EnrollmentJourney } from "@/services/analytics/enrollment";

// Inline ToS checkbox for the Enable Rewards (Mode 2) flow. Mirrors
// YaasPositionForm's TosCheckbox so both apply surfaces have the same
// affordance + copy. The backend's canonicalizeEnrollmentPayload
// rejects payloads with `tosAccepted !== true` (400 before signature
// verify), so the parent gates Submit on this checkbox.
export function EnableTosCheckbox({
    analytics,
    terms,
    checked,
    onCheckedChange,
    disabled,
    reopenKey = 0,
    forMultisig = false
}: {
    analytics?: EnrollmentJourney;
    terms: PublishedTermsDescriptor | null;
    checked: boolean;
    onCheckedChange: (next: boolean) => void;
    disabled: boolean;
    reopenKey?: number;
    /** A member wallet accepts for the multisig it is enrolling. */
    forMultisig?: boolean;
}): React.ReactElement {
    const id = useId();
    const [showTerms, setShowTerms] = useState(false);
    const previousReopenKey = useRef(reopenKey);

    useEffect(() => {
        if (reopenKey === previousReopenKey.current) return;
        previousReopenKey.current = reopenKey;
        setShowTerms(true);
    }, [reopenKey]);

    return (
        <>
            <div className="flex items-start gap-2 mt-6">
                <Checkbox
                    id={id}
                    checked={checked}
                    onCheckedChange={(next): void => {
                        analytics?.send("terms:checkbox_toggled", {
                            surface: "retail_enrollment",
                            control: "inline",
                            checked: next === true,
                            terms_version: terms?.version,
                            terms_document_key: terms?.documentKey,
                            terms_content_digest: terms?.contentDigest
                        });
                        onCheckedChange(next === true);
                    }}
                    disabled={disabled || !terms}
                    // Sit on the first line: the box is 20px, the
                    // `small` line is 20px, 24px from `md`.
                    className="shrink-0 md:mt-0.5"
                />
                {/* One text flow, not a flex row: a long sentence (the
                    multisig one) wraps like prose, and the link moves to the
                    next line whole instead of standing apart in its own
                    column. `block` overrides Label's flex layout. */}
                <Label htmlFor={id} className="block">
                    {forMultisig
                        ? "I agree on behalf of this multisig to the"
                        : "I agree to the"}{" "}
                    {/* Link + full stop never break apart, so the period
                        sits tight against the name. */}
                    <span className="inline-flex whitespace-nowrap">
                        {/* `size="sm"` so the button resolves to the same
                            `small` text variant `Label` uses; the two sat at
                            different sizes on desktop before. `link` resolves
                            its own underline interaction, so none is set here.

                            The label is the published document's own name,
                            so renaming the terms renames this link. It
                            resolves in yaas-api as
                            `coalesce(issuer_terms_reviews.name, 'Terms and
                            conditions')` — set the review row's name to
                            change what renders here. */}
                        <Button
                            variant="link"
                            size="sm"
                            // Inline in the sentence: give the DS label the
                            // text's line box and drop its 1px optical lift so
                            // the link sits on the sentence's baseline.
                            className="[&_[data-slot=button-label]]:leading-[inherit] [&_[data-slot=button-label]]:translate-y-0"
                            disabled={!terms}
                            onClick={(): void => setShowTerms(true)}
                        >
                            {terms?.name ?? "Terms"}
                        </Button>
                        .
                    </span>
                </Label>
            </div>
            <TermsOfServiceModal
                key={`${terms?.version}:${terms?.contentDigest}`}
                analytics={analytics}
                document={terms ?? undefined}
                open={showTerms}
                onClose={(): void => setShowTerms(false)}
                onAccept={(): void => {
                    setShowTerms(false);
                    onCheckedChange(true);
                }}
                surface="retail_enrollment"
                trigger="retail_enrollment_form"
            />
        </>
    );
}
